import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { capabilityLabelKey, domainLabelKey, levelLabelKey } from '@/shared/access';
import type { AccessDomain, AccessReviewRow, ResolvedDomain } from '@/shared/access';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { LEVEL_COLOR } from '../../lib/levelColor';
import { personFlagKey, personFlags, summariseAccess } from '../../lib/reviewFlags';

/** Which shape the employee listing takes. */
type EmployeeView = 'list' | 'matrix';

export interface EmployeeAccessPanelProps {
  /** The rows to show — already narrowed by the search and the "needs a look" filter. */
  rows: AccessReviewRow[];
  domains: AccessDomain[];
  total: number;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  onRetry: () => void;
  /** Drives which empty state explains why there is nothing here. */
  flaggedOnly: boolean;
  searching: boolean;
}

/**
 * "Should this person still have this?" — the second question of the access review.
 *
 * <p>It used to be a matrix of every employee against every domain, and that is the wrong default for a
 * company with a real number of either: twenty domains is twenty columns, so the table was as wide as it
 * was long and the reviewer scrolled sideways to find out whether anything was wrong.
 *
 * <p>So the default is a **list**: one fixed-width row per person carrying their strongest rights, with
 * everything else — every domain, the department each level actually came from, the capabilities that
 * came with it — one click away in a detail drawer. Provenance is the part a matrix cell could never
 * show, and it is exactly what the reviewer needs to act: "revoke this" is a decision about a department,
 * and the drawer is where the person's access is finally traced back to one.
 *
 * <p>The matrix is kept, one toggle away, because reading a single domain down a column ("everyone who
 * can change prices") is a genuine question the list answers badly. It lives in its own horizontally
 * scrolling container so the page body never scrolls sideways.
 */
export function EmployeeAccessPanel({
  rows,
  domains,
  total,
  page,
  size,
  onPageChange,
  onSizeChange,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  flaggedOnly,
  searching,
}: EmployeeAccessPanelProps) {
  const { t } = useTranslation('access');
  const [view, setView] = useState<EmployeeView>('list');
  const [selected, setSelected] = useState<AccessReviewRow | null>(null);

  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'flex-start' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{t('review.people.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('review.people.subtitle')}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_, next: EmployeeView | null) => next && setView(next)}
          aria-label={t('review.people.view.aria')}
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton value="list" aria-label={t('review.people.view.list')}>
            <ViewListRoundedIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="matrix" aria-label={t('review.people.view.matrix')}>
            <GridOnRoundedIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {isLoading ? (
        <LoadingState label={t('review.loading')} />
      ) : isError ? (
        <ErrorState title={t('review.loadError')} message={errorMessage} onRetry={onRetry} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            flaggedOnly
              ? t('review.noFlagged.title')
              : searching
                ? t('review.noMatches.title')
                : t('review.empty.title')
          }
          description={
            flaggedOnly
              ? t('review.noFlagged.description')
              : searching
                ? t('review.noMatches.description')
                : t('review.empty.description')
          }
        />
      ) : (
        <>
          {view === 'list' ? (
            <EmployeeList rows={rows} onSelect={setSelected} />
          ) : (
            <EmployeeMatrix rows={rows} domains={domains} />
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, next) => onPageChange(next)}
            rowsPerPage={size}
            onRowsPerPageChange={(event) => onSizeChange(Number(event.target.value))}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </>
      )}

      <PersonAccessDrawer person={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

/** The person's own identity cell — reused by both views so the flag marker cannot drift between them. */
function PersonCell({ row }: { row: AccessReviewRow }) {
  const { t } = useTranslation('access');
  const flags = personFlags(row);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {row.name ?? row.email}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.email}
        </Typography>
      </Box>
      {flags.length > 0 && (
        <Tooltip title={t(personFlagKey(flags[0]))}>
          <WarningAmberRoundedIcon fontSize="inherit" color="warning" />
        </Tooltip>
      )}
    </Stack>
  );
}

/** The default view: fixed width, one row per person, detail on demand. */
function EmployeeList({
  rows,
  onSelect,
}: {
  rows: AccessReviewRow[];
  onSelect: (row: AccessReviewRow) => void;
}) {
  const { t } = useTranslation('access');

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('review.table.employee')}</TableCell>
            <TableCell>{t('review.people.table.access')}</TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {t('review.people.table.details')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const summary = summariseAccess(row);
            return (
              <TableRow key={row.userId} hover>
                <TableCell>
                  <PersonCell row={row} />
                </TableCell>
                <TableCell>
                  {summary.total === 0 ? (
                    <Typography variant="caption" color="text.disabled">
                      {t('review.people.noAccess')}
                    </Typography>
                  ) : (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {summary.top.map((domain) => (
                        <Chip
                          key={domain.domainCode}
                          size="small"
                          color={LEVEL_COLOR[domain.level]}
                          label={`${t(domainLabelKey(domain.domainCode), domain.domainCode)} · ${t(
                            levelLabelKey(domain.level),
                          )}`}
                        />
                      ))}
                      {summary.more > 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('review.people.more', { count: summary.more })}
                        />
                      )}
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => onSelect(row)}
                    aria-label={t('review.people.detail.open', { name: row.name ?? row.email })}
                  >
                    <ChevronRightRoundedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * The wide view, kept for reading a single domain down a column.
 *
 * <p>Scrolls inside its own container, with the employee column pinned so a row stays identifiable at the
 * right-hand edge of a twenty-domain table.
 */
function EmployeeMatrix({ rows, domains }: { rows: AccessReviewRow[]; domains: AccessDomain[] }) {
  const { t } = useTranslation('access');

  return (
    <TableContainer sx={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 3 }}>
              {t('review.table.employee')}
            </TableCell>
            {domains.map((domain) => (
              <TableCell key={domain.code} align="center" sx={{ whiteSpace: 'nowrap' }}>
                {t(domainLabelKey(domain.code), domain.code)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.userId} hover>
              <TableCell
                sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 2 }}
              >
                <PersonCell row={row} />
              </TableCell>
              {domains.map((domain) => {
                const level = row.domains.find((d) => d.domainCode === domain.code)?.level;
                return (
                  <TableCell key={domain.code} align="center">
                    {level && level !== 'NONE' ? (
                      <Chip
                        size="small"
                        color={LEVEL_COLOR[level]}
                        label={t(levelLabelKey(level))}
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * One person's whole resolved access, including where each level came from.
 *
 * <p>Provenance is the reason this drawer exists rather than a wider row: rights are attached to
 * departments, so "Maria has Orders Admin" is not actionable until the screen also says *which
 * department gave it to her*. Vertical by construction, so it works unchanged on a narrow viewport.
 */
function PersonAccessDrawer({
  person,
  onClose,
}: {
  person: AccessReviewRow | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('access');

  return (
    <Drawer
      anchor="right"
      open={person !== null}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100%' } } }}
    >
      {person && (
        <Stack spacing={1.5} sx={{ p: 2 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6">{person.name ?? person.email}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {person.email}
              </Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={person.status} />
                {personFlags(person).map((flag) => (
                  <Chip
                    key={flag}
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={t(personFlagKey(flag))}
                  />
                ))}
              </Stack>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label={t('common:action.close')}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>

          {person.domains.length === 0 ? (
            <EmptyState title={t('review.people.detail.noAccess')} />
          ) : (
            person.domains.map((domain) => <DomainDetail key={domain.domainCode} domain={domain} />)
          )}
        </Stack>
      )}
    </Drawer>
  );
}

function DomainDetail({ domain }: { domain: ResolvedDomain }) {
  const { t } = useTranslation('access');
  const granted = domain.capabilityDecisions.filter((decision) => decision.granted);

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.25 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
          {t(domainLabelKey(domain.domainCode), domain.domainCode)}
        </Typography>
        <Chip
          size="small"
          color={LEVEL_COLOR[domain.level]}
          label={t(levelLabelKey(domain.level))}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {domain.levelSource?.type === 'GROUP'
          ? t('review.people.detail.source', {
              name: domain.levelSource.label ?? domain.levelSource.id,
            })
          : t('review.people.detail.direct')}
      </Typography>
      {granted.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
          {granted.map((decision) => (
            <Chip
              key={decision.capabilityCode}
              size="small"
              variant="outlined"
              label={t(capabilityLabelKey(decision.capabilityCode), decision.capabilityCode)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
